import { conversationExportRows } from './conversationImportFormat';

// Keep provider grouping separate from messages. IDs belong to the export,
// never to the destination account's collections.
const idOf = value => {
  if (value && typeof value === 'object') value = value.id ?? value._id ?? value.uuid ?? value.conversation_id ?? value.project_id ?? value.$oid;
  if (typeof value === 'number') value = String(value);
  return typeof value === 'string' && value.trim() ? value.trim() : null;
};

export function importMetadataKind(filename) {
  const base = filename.split('/').pop().toLowerCase();
  return /^(projects|collections|folders)(?:-\d+)?\.json$/.exec(base)?.[1] || null;
}

export function collectImportMetadata(raw) {
  const metadata = [...(raw?.collectionMetadata || [])];
  let container = raw?.object === 'conversation_import_archive' ? raw.conversations : raw;
  for (let depth = 0; container && !Array.isArray(container) && depth <= 5; depth++) {
    for (const kind of ['projects', 'collections', 'folders']) {
      if (Array.isArray(container[kind])) metadata.push({ kind, rows: container[kind] });
    }
    container = container.data ?? container.conversations;
  }
  return metadata;
}

export function parseImportCollections(source, raw, companionMetadata = []) {
  const collections = new Map();
  const memberships = new Map();
  const warnings = new Set();
  const rows = conversationExportRows(raw) || [];
  const metadata = [...companionMetadata, ...collectImportMetadata(raw)];

  const add = (record, fallbackId) => {
    if (!record || typeof record !== 'object') return;
    const externalId = idOf(record.externalId ?? record.id ?? record._id ?? record.uuid ?? record.project_id ?? record.project_uuid ?? record.gizmo_id ?? fallbackId);
    if (!externalId) { warnings.add('A folder without a source ID could not be restored.'); return; }
    const title = [record.title, record.name].find(value => typeof value === 'string' && value.trim())?.trim();
    const parentExternalId = idOf(record.parentExternalId ?? record.parentId ?? record.parent_id ?? record.parent_uuid);
    const prior = collections.get(externalId);
    if (prior?.title && title && (prior.title !== title || prior.parentExternalId !== parentExternalId)) {
      warnings.add(`Conflicting folder metadata for "${title}"; the first definition was kept.`);
    } else {
      collections.set(externalId, { externalId, title: title || prior?.title || null, parentExternalId });
    }
    const members = record.conversationIds || record.conversation_ids || record.conversations || [];
    if (!Array.isArray(members)) warnings.add(`Could not read chat membership for "${title || externalId}".`);
    for (const member of Array.isArray(members) ? members : []) {
      const conversationId = idOf(member);
      if (!conversationId) continue;
      if (memberships.has(conversationId) && memberships.get(conversationId) !== externalId) {
        warnings.add('A chat belongs to multiple source folders; only its first folder can be restored.');
      } else memberships.set(conversationId, externalId);
    }
  };

  for (const item of metadata) {
    const records = Array.isArray(item.rows) ? item.rows : item.rows?.[item.kind];
    if (!Array.isArray(records)) { warnings.add(`Could not read ${item.kind} metadata.`); continue; }
    for (const record of records) add(record);
  }

  const conversationCollectionIds = new Map();
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue;
    const conv = source === 'grok' ? row.conversation || row : row;
    const conversationId = idOf(conv.id ?? conv._id ?? conv.uuid ?? conv.conversation_id);
    let groupId = idOf(conv.inCollectionId ?? conv.project_id ?? conv.project_uuid ?? conv.projectId ?? conv.folder_id ?? conv.folderId ?? conv.collection_id ?? conv.collectionId ?? conv.project);
    // A gizmo may be a custom GPT. Only explicitly listed groups or the
    // Project prefix qualify; ordinary GPT conversations stay ungrouped.
    if (!groupId && typeof conv.gizmo_id === 'string' &&
        (conv.gizmo_id.startsWith('g-p-') || collections.has(conv.gizmo_id))) groupId = conv.gizmo_id;
    groupId ||= memberships.get(conversationId);
    if (!groupId) continue;
    if (conv.project && typeof conv.project === 'object') add(conv.project, groupId);
    if (!collections.has(groupId)) add({ id: groupId, name: conv.project_name ?? conv.projectName ?? conv.folder_name ?? conv.collection_name });
    if (conversationId) conversationCollectionIds.set(conversationId, groupId);
    // Retain identity for old exports whose chats have no stable ID.
    conversationCollectionIds.set(row, groupId);
  }
  for (const [conversationId, groupId] of memberships) {
    if (!conversationCollectionIds.has(conversationId)) conversationCollectionIds.set(conversationId, groupId);
  }
  for (const collection of collections.values()) {
    if (collection.parentExternalId && !collections.has(collection.parentExternalId)) {
      add({ id: collection.parentExternalId });
    }
  }
  for (const collection of collections.values()) {
    if (!collection.title) {
      collection.nameMissing = true;
      collection.title = `Imported ${source === 'chatgpt' ? 'ChatGPT' : source === 'claude' ? 'Claude' : source === 'grok' ? 'Grok' : 'Gab AI'} folder (${collection.externalId})`;
      warnings.add('The export omitted some folder names. Those folders were preserved with names based on their source IDs.');
    }
  }
  return { collections: [...collections.values()], conversationCollectionIds, collectionWarnings: [...warnings] };
}
